import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

interface CallRecordState {
    page?: number;
    size?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    employeeIds?: number[];
    clientIds?: number[];
    requesterIds?: number[];
    serviceIds?: number[];
    status?: string[];
    sortBy?: string;
    orderBy?: string;
    [key: string]: any;
}

@Injectable({
    providedIn: 'root'
})
export class CallRecordStateManagementService {
    private tableState = new BehaviorSubject<CallRecordState>({});
    currentState$ = this.tableState.asObservable().pipe(
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
    );
    private lastValidState: CallRecordState = {};
    private isUpdating = false;
    private lastPageVisited: string | null = null;


    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private location: Location
    ) {
        // Initialize state from URL on service creation
        this.route.queryParams.pipe(
            filter(() => !this.isUpdating),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        ).subscribe(params => {
            const state: CallRecordState = this.parseUrlParams(params);
            if (JSON.stringify(state) !== JSON.stringify(this.lastValidState)) {
                this.lastValidState = state;
                this.tableState.next(state);
            }
        });

        // Modify the navigation event listener
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
            // Get the current URL path segments
            const urlSegments = event.url.split('?')[0].split('/');
            const currentPage = urlSegments[urlSegments.length - 1];

            // Check if we're navigating between list and detail views
            const isDetailView = event.url.includes('/view-record/');
            const isCallRecordView = currentPage === 'callRecord';

            if (currentPage !== this.lastPageVisited) {
                if (this.lastPageVisited === 'callRecord' && !isCallRecordView && !isDetailView) {
                    // Navigating away from call records - clear state
                    this.clearState();
                } else if (isCallRecordView && !isDetailView) {
                    // Returning to call record list view - restore state
                    this.restoreState();
                }
            }

            // Store the current page for next comparison
            this.lastPageVisited = currentPage;
        });
    }

    private parseUrlParams(params: any): CallRecordState {
        const state: CallRecordState = {};
        if (params['page']) state.page = parseInt(params['page']);
        if (params['size']) state.size = parseInt(params['size']);
        if (params['search']) state.search = params['search'];
        if (params['startDate']) state.startDate = params['startDate'];
        if (params['endDate']) state.endDate = params['endDate'];

        // Parse array parameters
        if (params['filter[employee-ids]']) state.employeeIds = params['filter[employee-ids]'].split(',').map(Number);
        if (params['filter[client-ids]']) state.clientIds = params['filter[client-ids]'].split(',').map(Number);
        if (params['filter[requester-ids]']) state.requesterIds = params['filter[requester-ids]'].split(',').map(Number);
        if (params['filter[service-ids]']) state.serviceIds = params['filter[service-ids]'].split(',').map(Number);
        if (params['filter[status]']) state.status = params['filter[status]'].split(',');
        if (params['sortBy']) state.sortBy = params['sortBy'];
        if (params['orderBy']) state.orderBy = params['orderBy'];

        return state;
    }

    updateState(newState: Partial<CallRecordState>) {
        if (this.isUpdating) return;

        this.isUpdating = true;
        try {
            const currentState = this.tableState.value;
            let updatedState = { ...currentState, ...newState };

            // Remove page and size parameters when applying filters
            if (
                (newState.search !== undefined && newState.search !== currentState.search) ||
                (newState.startDate !== undefined && newState.startDate !== currentState.startDate) ||
                (newState.endDate !== undefined && newState.endDate !== currentState.endDate) ||
                (newState.employeeIds !== undefined && JSON.stringify(newState.employeeIds) !== JSON.stringify(currentState.employeeIds)) ||
                (newState.clientIds !== undefined && JSON.stringify(newState.clientIds) !== JSON.stringify(currentState.clientIds)) ||
                (newState.requesterIds !== undefined && JSON.stringify(newState.requesterIds) !== JSON.stringify(currentState.requesterIds)) ||
                (newState.serviceIds !== undefined && JSON.stringify(newState.serviceIds) !== JSON.stringify(currentState.serviceIds)) ||
                (newState.status !== undefined && JSON.stringify(newState.status) !== JSON.stringify(currentState.status)) ||
                (newState.orderBy !== undefined && newState.orderBy !== currentState.orderBy)
            ) {
                // Always set page to 1 when filters change, overriding any existing page value
                updatedState.page = 1;
                // Remove both page and size from state when filters change
                const { page, size, ...stateWithoutPagination } = updatedState;
                updatedState = { ...stateWithoutPagination, page: 1 };
            }

            if (JSON.stringify(updatedState) !== JSON.stringify(this.lastValidState)) {
                this.lastValidState = updatedState;
                this.tableState.next(updatedState);
                this.updateUrl(updatedState);
            }
        } finally {
            this.isUpdating = false;
        }
    }

    private shouldResetPagination(currentState: CallRecordState, newState: Partial<CallRecordState>): boolean {
        return (
            (newState.search !== undefined && newState.search !== currentState.search) ||
            (newState.startDate !== undefined && newState.startDate !== currentState.startDate) ||
            (newState.endDate !== undefined && newState.endDate !== currentState.endDate) ||
            this.hasArrayChanged(newState.employeeIds, currentState.employeeIds) ||
            this.hasArrayChanged(newState.clientIds, currentState.clientIds) ||
            this.hasArrayChanged(newState.requesterIds, currentState.requesterIds) ||
            this.hasArrayChanged(newState.serviceIds, currentState.serviceIds) ||
            this.hasArrayChanged(newState.status, currentState.status)
        );
    }

    private hasArrayChanged(newArray?: any[], currentArray?: any[]): boolean {
        if (newArray === undefined) return false;
        if (!currentArray) return newArray.length > 0;
        return JSON.stringify(newArray) !== JSON.stringify(currentArray);
    }

    getQueryParams(): CallRecordState {
        return this.lastValidState;
    }

    private updateUrl(state: CallRecordState) {
        const cleanState = Object.entries(state)
            .filter(([_, value]) => value != null && value !== '' &&
                (Array.isArray(value) ? value.length > 0 : true))
            .reduce((acc, [key, value]) => ({
                ...acc,
                [key]: Array.isArray(value) ? value.join(',') : value
            }), {});

        this.location.replaceState(
            this.router.createUrlTree([], {
                relativeTo: this.route,
                queryParams: cleanState,
                queryParamsHandling: 'merge'
            }).toString()
        );
    }

    private restoreState() {
        if (Object.keys(this.lastValidState).length > 0) {
            this.updateUrl(this.lastValidState);
            if (JSON.stringify(this.tableState.value) !== JSON.stringify(this.lastValidState)) {
                this.tableState.next(this.lastValidState);
            }
        }
    }

    clearState() {
        this.lastValidState = {};
        this.tableState.next({});
        // this.updateUrl({});

        // Clear URL parameters
        this.location.replaceState(
            this.router.createUrlTree([], {
                relativeTo: this.route,
                queryParams: {}
            }).toString()
        );
    }
}